
import { useCallback, useEffect, useState } from 'react';
import { usePermissions, useTranslate } from '../../customHooks';
import { useHistory, withRouter } from 'react-router-dom';
import { toast } from '../../components';
import { Tooltip } from '@panwds/react-ui';
import { PANWDSTable } from '../../components/PANWDSElements';
import { dataProvider } from '../../dataProvider';
import { nameStyleCursor } from '../../layout/styles';
import { RouteUri } from '../../routeUri';
import get from 'lodash/get';

const CertificateList = (props: any) => {
  const nameClass = nameStyleCursor();

  const translate = useTranslate();
  const history = useHistory();
  const { permissions } = usePermissions();
  const title = translate(`resources.certificate.name`)

  const [gridData, setGridData] = useState([]);  const [loading, setLoading] = useState(false);
  const [nextToken, setNextToken]: any = useState();

  // @ts-ignore
  const [gridRef, setGridRef] = useState({
    current: {
      columns: []
    },
  });

  // get the rulestack name from route params
  const ruleStackName = props.match.params.rulestackname;

  const deleteAction = (selected: any) => {
    if (!permissions?.DeleteCertificateObject) {
      toast.warning(translate("permissions.cantExecute"));
      return;
    }
    if (selected && Array.isArray(selected)) {
      let calls: any = [];
      setLoading(true);
      selected.map((item: any) => {
        if (item.Name) {
          calls.push(dataProvider.delete("certificate", { id: item.Name, previousData: {...item, RuleStackName: ruleStackName} }));
        }
        return item;
      });
      Promise.all(calls)
        .then((responses) => toast.success(translate(`resources.certificate.delete.success`)))
        .catch((e) => toast.error(e?.error, { toastId: "certificate-delete" }))
        .finally(() => {
          setLoading(false);
          loadGridData(true);
        });
    }
  }

  const getcolumns = () => [
    {
      accessor: 'Name',
      Header: translate(`resources.certificate.fields.Name`),
      columnSize: 2,
      render: ({ row }: any) => <span
        className={nameClass.blueColor}
        onClick={(evt) => onRowClick(row, evt)}>
          {row?.original?.Name}
      </span>,
    },
    {
      accessor: 'CertificateObjectCandidate.CertificateSignerArn',
      Header: translate(`resources.certificate.fields.CertificateSignerArn`),
      columnSize: 4,
      minWidth: 150,
      noTooltip: true,
      render: ({ row }: any) => {
        if (row?.original?.CertificateObjectCandidate?.CertificateSignerArn) {
          return <Tooltip label={row?.original?.CertificateObjectCandidate?.CertificateSignerArn}>
            <span>
              {row?.original?.CertificateObjectCandidate?.CertificateSignerArn}
            </span>
          </Tooltip>
        }
        return null;
      },
    },
    {
      accessor: 'CertificateObjectCandidate.CertificateSelfSigned',
      Header: translate(`resources.certificate.fields.CertificateSelfSigned`),
      columnSize: 1,
      render: ({ row }: any) => {
        if (row?.original?.CertificateObjectCandidate) {
          return <span>{(row?.original?.CertificateObjectCandidate?.CertificateSelfSigned) ? 'Yes' : 'No'}</span>
        }
        return null;
      }
    },
    {
      accessor: 'CertificateObjectCandidate.Description',
      Header: translate(`resources.certificate.fields.Description`),
      columnSize: 5,
      render: ({ row }: any) => {
        if (row?.original?.CertificateObjectCandidate?.Description) {
          return <span>{row?.original?.CertificateObjectCandidate?.Description}</span>
        }
        return null;
      },
    }
  ];

  const loadGridData = (needRefresh=false) => {
    if (!permissions?.ListCertificateObject) {
      if (permissions && !permissions?.ListCertificateObject) {
        // TODO toast is not showing, but if we add toast container it will show
        // many times until permissions is loaded, need to rethink this logic
        toast.warning(translate("permissions.cantView"));
      }
      setLoading(false);
      return;
    }
    if ((gridData.length && nextToken) || !gridData.length || needRefresh) {
      if (!gridData.length || needRefresh) {
        setLoading(true);
      }
      dataProvider.getList("certificate", {
        data: { RuleStackName: ruleStackName, NextToken: (nextToken) ? nextToken : undefined }
      }).then(async (response: any) => {
        if (response.data) {
          //@ts-ignore
          let responseObj = response.data;
          if (needRefresh){
            setGridData(responseObj);
          } else {
            //@ts-ignore
            setGridData([...gridData, ...responseObj]);
          }
          setNextToken(response?.nextToken);
        } else {
          toast.error(response?.error, { toastId: "certificate-getList" });
        }
      })
      .catch((e:any) => {
        if (e?.noToast) {
          return;
        }
        toast.error(e?.error, { toastId: "certificate-getList" });
      })
      .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }

  useEffect(() => {
    return () => {
      dataProvider.abort('certificate');
    }
  }, []);

  const onRowClick = useCallback((rowProps, event) => {
    if (!permissions?.DescribeCertificateObject) {
      return;
    }
    history.push(
      RouteUri.CertificateEdit.replace(":rulestackname", ruleStackName)
        .replace(":certificatename", rowProps.original.Name));
  }, [history, ruleStackName, permissions]);

  const getGridDataValue = (name: string, data: any) => {
    let value = get(data, name);
    if (value) return value + '';
    return '';
  }

  const toolbarActions: any[] = [];
  if (permissions?.DeleteCertificateObject) {
    toolbarActions.push({
      title: translate(`common.actions`),
      dataMetrics: "cloudngfw-certificate-actions-btn",
      actionsMap: [{ menuText: translate(`common.delete`), handleAction: deleteAction, dataMetrics: "cloudngfw-certificate-delete-btn", },]
    });
  }
  if (permissions?.CreateCertificateObject) {
    toolbarActions.push({
      title: translate(`resources.certificate.fields.CreateButton`),
      actionsMap: [],
      dataMetrics: "cloudngfw-certificate-create-btn",
      handleButtonAction: () => history.push(RouteUri.CertificateCreate.replace(":rulestackname", ruleStackName)),
      type: 'button',
      appearance: 'primary'
    });
  }

  return (
    <>
      <PANWDSTable
        setHandle={setGridRef}
        loadGridData={loadGridData}
        infiniteScroll={false}
        overflowTable={true}
        permissions={permissions}
        columns={getcolumns()}
        gridData={gridData}
        showSelectGroupBy={false}
        title={title}
        subtitle={translate(`resources.certificate.fields.ListSubtitle`)}
        idProperty={"RuleStackName"}
        showToolbar={true}
        dropDownActionsArray={toolbarActions}
        showTileTitle={true}
        emptyTitle={translate(`resources.certificate.fields.EmptyTitle`)}
        emptySubtitle={translate(`resources.certificate.fields.EmptySubtitle`)}
        loading={loading}
        isBackgroundFetching={nextToken}
        singleSelect={false}
        multiSelect={true}
        useOldToolbarButtons={true}
        offsetTableHeight={305}
        tableMinHeight={550}
        searchFilterRequired={true}
        dataMetrics="cloudngfw-certificate-table"
        dataTestId="cloudngfw-certificate-table"
      />
    </>
  );
};

export default withRouter(CertificateList);
